import React, { useMemo, useState } from "react";
import { isAxiosError } from "axios";
import DOMPurify from "dompurify";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { Button } from "components/common/Button";
import { tos, privacyPolicy, LegalDocument } from "./legalContent";

interface ConsentFormProps {
  onAccepted: () => void;
  compact?: boolean;
}

const InfoIcon = () => (
  <svg
    className="mt-0.5 shrink-0"
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 4.5V9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
  </svg>
);

interface DocTabProps {
  label: string;
  active: boolean;
  hasChanges: boolean;
  onClick: () => void;
}

const DocTab = ({ label, active, hasChanges, onClick }: DocTabProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs cursor-pointer bg-transparent transition-colors duration-150"
    style={{
      fontWeight: 500,
      color: active ? "var(--color-accent)" : "var(--color-fg-muted)",
      borderBottom: active
        ? "2px solid var(--color-accent)"
        : "2px solid transparent",
      fontFamily: "inherit",
    }}
  >
    {label}
    {hasChanges && (
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "var(--color-accent)",
        }}
      />
    )}
  </button>
);

interface DocPanelProps {
  doc: LegalDocument;
  compact: boolean;
}

const DocPanel = ({ doc, compact }: DocPanelProps) => {
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(doc.html), [doc.html]);

  return (
    <div className="flex flex-col gap-2.5" style={{ paddingTop: 12 }}>
      {doc.changeSummary && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-app text-xs leading-relaxed"
          style={{
            backgroundColor:
              "var(--color-accent-subtle, rgba(59,130,246,0.08))",
            border:
              "1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)",
            color: "var(--color-accent)",
          }}
        >
          <InfoIcon />
          <span>{doc.changeSummary}</span>
        </div>
      )}

      <div
        className="rounded-app text-xs overflow-y-auto overscroll-contain legal-content"
        style={{
          maxHeight: compact ? "200px" : "280px",
          border: "1px solid var(--color-border)",
          backgroundColor: "var(--color-bg-subtle)",
          padding: "16px",
          scrollbarWidth: "thin",
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />

      <span
        className="text-[10px] text-fg-muted-medium px-2 py-0.5 rounded-app self-start"
        style={{ backgroundColor: "var(--color-bg-hover)" }}
      >
        v{doc.version} · Effective {doc.effectiveDate}
      </span>
    </div>
  );
};

type TabId = "tos" | "pp";

export const ConsentForm = ({
  onAccepted,
  compact = false,
}: ConsentFormProps) => {
  const { setTosAccepted, setPpAccepted } = useAuth();

  const [activeTab, setActiveTab] = useState<TabId>("tos");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setError("");

    try {
      await axiosInstance.post("/action/auth/accept-consent", {
        consent_types: ["tos", "privacy_policy"],
      });
      setTosAccepted(true);
      setPpAccepted(true);
      onAccepted();
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 401) {
        return;
      }
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Tabs */}
      <div>
        <div
          className="flex"
          style={{ borderBottom: "1px solid var(--color-border)" }}
        >
          <DocTab
            label="Terms of Service"
            active={activeTab === "tos"}
            hasChanges={!!tos.changeSummary}
            onClick={() => setActiveTab("tos")}
          />
          <DocTab
            label="Privacy Policy"
            active={activeTab === "pp"}
            hasChanges={!!privacyPolicy.changeSummary}
            onClick={() => setActiveTab("pp")}
          />
        </div>

        {activeTab === "tos" && <DocPanel doc={tos} compact={compact} />}
        {activeTab === "pp" && (
          <DocPanel doc={privacyPolicy} compact={compact} />
        )}
      </div>

      {/* Checkbox + Accept */}
      <div className="flex flex-col gap-4">
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 min-w-4 rounded-app cursor-pointer accent-accent"
            style={{ border: "1px solid var(--color-border)" }}
          />
          <span className="text-xs text-fg leading-relaxed">
            I have read and agree to the{" "}
            <a
              href="#"
              className="text-accent hover:underline"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("tos");
              }}
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="#"
              className="text-accent hover:underline"
              onClick={(e) => {
                e.preventDefault();
                setActiveTab("pp");
              }}
            >
              Privacy Policy
            </a>
          </span>
        </label>

        <Button
          variant="primary"
          size="lg"
          onClick={handleAccept}
          disabled={!agreed || submitting}
          aria-disabled={!agreed || submitting}
          className="w-full"
        >
          {submitting ? "Accepting..." : "Accept & Continue"}
        </Button>
      </div>

      {error && (
        <div
          className="p-3 rounded-app"
          style={{
            backgroundColor:
              "color-mix(in srgb, var(--color-error) 10%, transparent)",
            border:
              "1px solid color-mix(in srgb, var(--color-error) 20%, transparent)",
          }}
        >
          <p className="text-xs text-error">{error}</p>
        </div>
      )}
    </div>
  );
};
